/**
 * Studio editor state normalizers, extracted from App.js so they can be shared
 * by the React app and by standalone tooling (e.g. the round-trip publish
 * script in scripts/). These are pure functions — no React/DOM dependencies —
 * and must stay behaviorally identical to what the UI runs.
 */
import { buildSignaturesTemplate } from "./signatureUtils";

/**
 * Collapse sections that share an attributeName (case-insensitive), keeping the
 * first occurrence. Returns the deduped list plus a map from each dropped
 * section id to the kept section id so field.sectionId references can be remapped.
 */
export const deduplicateSectionsByAttributeName = (sectionList) => {
  const seen = new Set();
  const kept = [];
  const sectionIdMap = {};
  for (const s of sectionList) {
    const key = (s.attributeName || "").toLowerCase();
    if (seen.has(key)) {
      const keptSection = kept.find((k) => (k.attributeName || "").toLowerCase() === key);
      if (keptSection) sectionIdMap[s.id] = keptSection.id;
    } else {
      seen.add(key);
      kept.push(s);
    }
  }
  return { sections: kept, sectionIdMap };
};

/**
 * Merge incoming signatures over the canonical signatures template so each role
 * carries the full set of template keys, while preserving any role/signature
 * overrides (min/max/default/altSigners/etc.) and extra signer rows present in
 * the incoming data.
 */
export const normalizeSignatures = (nextSignatures) => {
  if (!nextSignatures || typeof nextSignatures !== "object") return {};
  const template = buildSignaturesTemplate();
  return Object.fromEntries(
    Object.entries(nextSignatures).map(([roleKey, roleValue]) => {
      const roleTemplate = template[roleKey];
      if (!roleTemplate || !roleValue) return [roleKey, roleValue];
      const existingSignatures = Array.isArray(roleValue.signatures)
        ? roleValue.signatures
        : [];
      const existingByKey = new Map(
        existingSignatures
          .filter((sig) => sig?.signerKey)
          .map((sig) => [sig.signerKey, sig]),
      );
      const templateSignatures = Array.isArray(roleTemplate.signatures)
        ? roleTemplate.signatures
        : [];
      const mergedTemplateSignatures = templateSignatures
        .filter((sig) => existingByKey.has(sig.signerKey))
        .map((sig) => ({ ...sig, ...existingByKey.get(sig.signerKey) }));
      const templateKeys = new Set(
        templateSignatures.map((sig) => sig.signerKey),
      );
      const extraSignatures = existingSignatures.filter(
        (sig) => sig?.signerKey && !templateKeys.has(sig.signerKey),
      );
      const altTemplate = Array.isArray(roleTemplate.altSigners) ? roleTemplate.altSigners : [];
      const altFromRole = Array.isArray(roleValue.altSigners) ? roleValue.altSigners : null;
      const altSigners = (
        altFromRole != null
          ? altFromRole
          : altTemplate
      )
        .map((x) => String(x || "").trim())
        .filter(Boolean);
      return [
        roleKey,
        {
          ...roleTemplate,
          ...roleValue,
          min: roleValue.min ?? roleTemplate.min,
          max: roleValue.max ?? roleTemplate.max,
          default: roleValue.default ?? roleTemplate.default,
          additionalSignaturePage: roleValue.additionalSignaturePage ?? roleTemplate.additionalSignaturePage,
          integrationRole: roleValue.integrationRole ?? roleTemplate.integrationRole,
          meviDealRole: roleValue.meviDealRole ?? roleTemplate.meviDealRole,
          altSigners,
          signatures: [...mergedTemplateSignatures, ...extraSignatures],
        },
      ];
    }),
  );
};
